// src/views/FullBookingPage/components/ServicesSection/index.jsx
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { currencyToSymbol } from "helpers/currencyToSymbol";
import CustomDropdown from "../../../shared/CustomDropdown";
import ServiceSvgIcon from "../../../../ServiceSvgIcon";
import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";

// Skeleton Loader Components
const ServiceCardSkeleton = () => {
  return (
    <div className={`${styles.serviceCard} ${styles.skeletonCard}`}>
      <div className={styles.serviceContent}>
        <div className={styles.serviceIconSkeleton}></div>
        <div className={styles.serviceDetails}>
          <div className={styles.serviceNameSkeleton}></div>
          <div className={styles.servicePriceSkeleton}></div>
          <div className={styles.serviceDescSkeleton}></div>
        </div>
        <div className={styles.serviceActionSkeleton}></div>
      </div>
    </div>
  );
};

const ServicesSectionSkeleton = ({ t }) => {
  return (
    <div className={styles.servicesSection}>
      <div className={styles.sectionHeader}>
        <div className={styles.headerContent}>
          <div className={styles.titleSkeleton}></div>
          <div className={styles.subtitleSkeleton}></div>
        </div>
      </div>
      
      <div className={styles.servicesGroup}>
        <div className={styles.groupTitleSkeleton}></div>
        <div className={styles.servicesList}>
          <ServiceCardSkeleton />
          <ServiceCardSkeleton />
          <ServiceCardSkeleton />
        </div>
      </div>
      
      <div className={styles.servicesGroup}>
        <div className={styles.groupTitleSkeleton}></div>
        <div className={styles.servicesList}>
          <ServiceCardSkeleton />
          <ServiceCardSkeleton />
        </div>
      </div>
    </div>
  );
};

const ServiceCard = ({ service, selectedQuantity, onSelect, currency, t, isRefreshing, capacity }) => {
  const [isAdded, setIsAdded] = useState(selectedQuantity > 0);
  const [quantity, setQuantity] = useState(selectedQuantity || 1);
  const localPendingRef = useRef(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!localPendingRef.current) {
      setIsAdded(selectedQuantity > 0);
      setQuantity(selectedQuantity || 1);
    }
  }, [selectedQuantity]);

  // Check if service is paid onsite
  const isPaidOnsite = useMemo(() => {
    return service.service?.location_type === "onsite" || 
           service.service?.type === "onsite" ||
           service.service?.place_service?.type === "onsite";
  }, [service]);

  const getServiceLabel = (serviceItem) => {
    try {
      const unitServiceIds = [
        "d9298be9-3749-47f4-8ef7-42197b36fd3d",
        "93a76791-6209-4ed4-8b5a-7a1a15bd5e4e",
        "2bae8e81-2a85-49c9-8579-a3092ffcc61d",
        "d491b21b-dede-4b31-8017-2a2ea2ee8984",
        "b536732a-0acd-4d02-810f-2fbb81625fe8",
        "d5a363d6-a232-4dfb-a20d-4a69b3943e34",
      ];

      if (!serviceItem?.service?.place_service?.location) {
        return unitServiceIds.includes(serviceItem?.service?.place_service?.id)
          ? t("space.services.byUnit")
          : "";
      }

      const formattedLocationType = serviceItem.service?.location_type
        ?.split("_")
        ?.map((word, index) =>
          index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
        )
        ?.join("");

      return serviceItem.service?.location_type === "included"
        ? t("space.services.included")
        : t(`booking.step2.units.${formattedLocationType}`);
    } catch (error) {
      console.error("Error generating service label:", error);
      return "";
    }
  };

  const getQuantityOptions = () => {
    if (service.values && Object.keys(service.values).length > 0) {
      return Object.keys(service.values).map(k => ({ 
        value: parseInt(k), 
        label: getQuantityLabel(parseInt(k), service)
      }));
    }
    
    if (capacity && capacity > 0) {
      const options = [];
      const maxQuantity = Math.min(capacity, 100);
      for (let i = 0; i <= maxQuantity; i++) {
        options.push({
          value: i,
          label: getQuantityLabel(i, service)
        });
      }
      return options;
    }
    
    return [
      { value: 1, label: getQuantityLabel(1, service) },
      { value: 2, label: getQuantityLabel(2, service) },
      { value: 3, label: getQuantityLabel(3, service) },
      { value: 4, label: getQuantityLabel(4, service) },
      { value: 5, label: getQuantityLabel(5, service) }
    ];
  };

  const getQuantityLabel = (quantityValue, serviceItem) => {
    const label = getServiceLabel(serviceItem);
    if (label) {
      return `${quantityValue} ${label}`;
    }
    return t("booking.fullBookingPage.services.quantityUnit", { count: quantityValue });
  };

  const basePrice = service.service?.price || 0;
  const hasValues = service.values && Object.keys(service.values).length > 0;
  const isFree = !hasValues && basePrice === 0;
  const firstQuantityPrice = hasValues ? service.values[Object.keys(service.values)[0]] : null;
  const isFreeWithQuantity = hasValues && firstQuantityPrice === 0;
  const isFreeService = isFree || isFreeWithQuantity;

  const getPriceForQuantity = (qty) => {
    if (service.values && service.values[qty] !== undefined) {
      return service.values[qty];
    }
    return service.service?.price * qty || 0;
  };

  const price = getPriceForQuantity(quantity);
  
  const description = service.service?.place_service?.description || 
                      service.service?.description || 
                      service.description || 
                      "";

  const handleAdd = () => {
    setIsAdded(true);
    onSelect(service.service.place_service.id, quantity);
  };

  const handleRemove = () => {
    setIsAdded(false);
    onSelect(service.service.place_service.id, 0);
  };

  const handleInclude = () => {
    setIsAdded(true);
    onSelect(service.service.place_service.id, 1);
  };

  const handleExclude = () => {
    setIsAdded(false);
    onSelect(service.service.place_service.id, 0);
  };

  const handleQuantityChange = (newQuantity) => {
    setQuantity(newQuantity);
    onSelect(service.service.place_service.id, newQuantity);
  };

  const renderIcon = () => {
    return (
      <ServiceSvgIcon
        iconPath={service.service.place_service.image_url}
        color="var(--main-color, #d51b81)"
        backgroundColor="transparent"
        size="small"
      />
    );
  };

  // For free services (included services) - No badge
  if (isFreeService) {
    return (
      <div className={`${styles.serviceCard} ${isAdded ? styles.added : ""} ${isRefreshing ? styles.refreshing : ""}`}>
        <div className={styles.serviceContent}>
          <div className={styles.serviceIcon}>
            {renderIcon()}
          </div>
          
          <div className={styles.serviceDetails}>
            <div className={styles.serviceHeader}>
              <h4 className={styles.serviceName}>
                {service.service.place_service.name}
              </h4>
            </div>
            
            {description && (
              <p className={styles.serviceDescription}>
                {description}
              </p>
            )}
          </div>
          
          <div className={styles.serviceAction}>
            {!isAdded ? (
              <button className={styles.includeButton} onClick={handleInclude} disabled={isRefreshing}>
                + {t("booking.fullBookingPage.services.include")}
              </button>
            ) : (
              <button className={styles.excludeButton} onClick={handleExclude} disabled={isRefreshing}>
                {t("booking.fullBookingPage.services.exclude")}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // For paid services
  return (
    <div className={`${styles.serviceCard} ${isAdded ? styles.added : ""} ${isRefreshing ? styles.refreshing : ""}`}>
      <div className={styles.serviceContent}>
        <div className={styles.serviceIcon}>
          {renderIcon()}
        </div>
        
        <div className={styles.serviceDetails}>
          <div className={styles.serviceHeader}>
            <h4 className={styles.serviceName}>
              {service.service.place_service.name}
            </h4>
            {/* ✅ Paid Onsite badge with tooltip */}
            {isPaidOnsite && (
              <span 
                className={styles.paidOnsiteBadge}
                title={t("booking.fullBookingPage.services.paidOnsiteTooltip", "This service is paid directly at the venue")}
              >
                💳 {t("booking.fullBookingPage.services.paidOnsite", "Pay on-site")}
              </span>
            )}
          </div>
          
          <div className={styles.servicePriceInline}>
            <span className={styles.priceAmountInline}>
              {price} {currencyToSymbol(currency)}
            </span>
          </div>
          
          {description && (
            <p className={styles.serviceDescription}>
              {description}
            </p>
          )}
          
          {isAdded && (
            <div className={styles.quantitySelector}>
              <CustomDropdown
                options={getQuantityOptions()}
                value={quantity}
                onChange={handleQuantityChange}
                placeholder={t("booking.fullBookingPage.services.quantity")}
                small
                disabled={isRefreshing}
              />
            </div>
          )}
        </div>
        
        <div className={styles.serviceAction}>
          {!isAdded ? (
            <button className={styles.addButton} onClick={handleAdd} disabled={isRefreshing}>
              + {t("booking.fullBookingPage.services.add")}
            </button>
          ) : (
            <button className={styles.removeButton} onClick={handleRemove} disabled={isRefreshing}>
              {t("booking.fullBookingPage.services.remove")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const DateButton = ({ label, onClick, isActive, isRefreshing }) => {
  return (
    <button 
      className={`${styles.dateButton} ${isActive ? styles.active : ""} ${isRefreshing ? styles.refreshing : ""}`} 
      onClick={onClick}
      disabled={isRefreshing}
    >
      {label}
    </button>
  );
};

const ServicesSection = ({ 
  servicesData, 
  selectedServiceDayId, 
  getSelectedQuantity, 
  handleServiceSelect,
  spaceCurrency,
  bookingHours,
  bookingDays,
  onDayChange,
  t,
  isLoading = false,
  capacity = 1
}) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [showSkeleton, setShowSkeleton] = useState(false);
  
  // ✅ ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURN
  // Create dayIdToDateMap using useMemo - moved BEFORE early returns
  const dayIds = useMemo(() => Object.keys(servicesData || {}), [servicesData]);
  
  const dayIdToDateMap = useMemo(() => {
    const map = {};
    
    // First, try to use bookingDays from backend (most reliable)
    if (bookingDays && bookingDays.length > 0) {
      bookingDays.forEach((day) => {
        try {
          const date = new Date(day.date);
          const formattedDate = date.toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          });
          map[day.id] = formattedDate;
        } catch (e) {
          map[day.id] = day.date || `Day ${day.day || 0}`;
        }
      });
    }
    
    // If no bookingDays, fallback to bookingHours
    if (Object.keys(map).length === 0 && bookingHours && bookingHours.length > 0) {
      bookingHours.forEach((slot, index) => {
        // Find the day ID by matching date
        const matchingDayId = dayIds.find(dayId => {
          return index < dayIds.length;
        });
        
        if (matchingDayId) {
          try {
            const date = new Date(slot.date);
            const formattedDate = date.toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            });
            map[matchingDayId] = formattedDate;
          } catch (e) {
            map[matchingDayId] = slot.date || `Day ${index + 1}`;
          }
        }
      });
    }
    
    // If still no mapping, use fallback labels
    dayIds.forEach((dayId, index) => {
      if (!map[dayId]) {
        map[dayId] = t("booking.fullBookingPage.services.day", { number: index + 1 });
      }
    });
    
    return map;
  }, [dayIds, bookingDays, bookingHours, t]);

  // Show skeleton when services are being reloaded
  useEffect(() => {
    if (isLoading) {
      setShowSkeleton(true);
    } else {
      setTimeout(() => {
        setShowSkeleton(false);
        setRefreshKey(prev => prev + 1);
      }, 300);
    }
  }, [isLoading]);

  // ✅ EARLY RETURNS AFTER ALL HOOKS
  // Show skeleton while loading
  if (showSkeleton) {
    return <ServicesSectionSkeleton t={t} />;
  }

  if (!servicesData || Object.keys(servicesData).length === 0) {
    return (
      <div className={styles.servicesSection}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.title}>
            {t("booking.fullBookingPage.services.title")}
          </h2>
          <p className={styles.subtitle}>
            {t("booking.fullBookingPage.services.subtitle")}
          </p>
        </div>
        <div className={styles.emptyState}>
          <p>{t("booking.fullBookingPage.services.noServices")}</p>
        </div>
      </div>
    );
  }

  if (!selectedServiceDayId || !servicesData[selectedServiceDayId]) {
    return (
      <div className={styles.servicesSection}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.title}>
            {t("booking.fullBookingPage.services.title")}
          </h2>
          <p className={styles.subtitle}>
            {t("booking.fullBookingPage.services.subtitle")}
          </p>
        </div>
        <div className={styles.loadingState}>
          <div className={styles.spinner}></div>
          <p>{t("booking.fullBookingPage.services.loading")}</p>
        </div>
      </div>
    );
  }

  const services = servicesData[selectedServiceDayId] || [];

  // Filter free services (included services)
  const freeServices = services.filter(item => {
    const basePrice = item?.service?.price || 0;
    const hasValues = item?.values && Object.keys(item.values).length > 0;
    const firstQuantityPrice = hasValues ? item.values[Object.keys(item.values)[0]] : null;
    const isIncluded = item.service?.location_type === "included";
    return isIncluded || (basePrice === 0 && !hasValues) || (hasValues && firstQuantityPrice === 0);
  });
  
  // Filter paid services
  const paidServices = services.filter(item => {
    const basePrice = item?.service?.price || 0;
    const hasValues = item?.values && Object.keys(item.values).length > 0;
    const firstQuantityPrice = hasValues ? item.values[Object.keys(item.values)[0]] : null;
    const isIncluded = item.service?.location_type === "included";
    return !isIncluded && ((basePrice > 0) || (hasValues && firstQuantityPrice > 0));
  });

  if (services.length === 0) return null;

  return (
    <div className={styles.servicesSection} key={refreshKey}>
      <div className={styles.sectionHeader}>
        <div className={styles.headerContent}>
          <h2 className={styles.title}>
            {t("booking.fullBookingPage.services.title")}
          </h2>
          <p className={styles.subtitle}>
            {t("booking.fullBookingPage.services.subtitle")}
          </p>
        </div>
        
        {dayIds.length > 1 && (
          <div className={styles.daySelector}>
            {dayIds.map((dayId) => {
              const dateLabel = dayIdToDateMap[dayId] || t("booking.fullBookingPage.services.day", { number: dayIds.indexOf(dayId) + 1 });
              
              return (
                <DateButton 
                  key={dayId}
                  label={dateLabel}
                  onClick={() => onDayChange(dayId)}
                  isActive={selectedServiceDayId === dayId}
                  isRefreshing={isLoading}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Free / Included Services Section */}
      {freeServices.length > 0 && (
        <div className={styles.servicesGroup}>
          <h3 className={styles.groupTitle}>
            {t("booking.fullBookingPage.services.freeServices")}
            {freeServices.length > 0 && <span className={styles.serviceCount}>({freeServices.length})</span>}
          </h3>
          <div className={styles.servicesList}>
            {freeServices.map((service) => (
              <ServiceCard
                key={`${service.service.place_service.id}-${refreshKey}`}
                service={service}
                selectedQuantity={getSelectedQuantity(service.service.place_service.id, selectedServiceDayId)}
                onSelect={(id, quantity) => handleServiceSelect(id, quantity, selectedServiceDayId)}
                currency={spaceCurrency}
                t={t}
                isRefreshing={isLoading}
                capacity={capacity}
              />
            ))}
          </div>
        </div>
      )}

      {/* Paid Services Section */}
      {paidServices.length > 0 && (
        <div className={styles.servicesGroup}>
          <h3 className={styles.groupTitle}>
            {t("booking.fullBookingPage.services.paidServices")}
            {paidServices.length > 0 && <span className={styles.serviceCount}>({paidServices.length})</span>}
          </h3>
          <div className={styles.servicesList}>
            {paidServices.map((service) => (
              <ServiceCard
                key={`${service.service.place_service.id}-${refreshKey}`}
                service={service}
                selectedQuantity={getSelectedQuantity(service.service.place_service.id, selectedServiceDayId)}
                onSelect={(id, quantity) => handleServiceSelect(id, quantity, selectedServiceDayId)}
                currency={spaceCurrency}
                t={t}
                isRefreshing={isLoading}
                capacity={capacity}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesSection;